/**
 * Inspector for a selected free element (flooring / counter / wall / object).
 * Flooring gets a floor-type select; whatever stands on its base gets height + base;
 * objects also get name, subtype, rotation, and whether they are movable.
 * Unpicking a flooring type stores '', which the save path strips, so the
 * region inherits the room's floor.
 */

import { css } from '@emotion/react';
import { FloorPlanFreeElement, freeElementBase, freeElementHeight, isMovableObject, movesInHeight, OBJECT_SUBTYPES } from '@kaiizen/kai-floor-plan';
import {
  capitalized,
  fmt,
  OBJECT_SUBTYPE_OPTIONS,
  setInnerFloorType,
  setInnerName,
  setInnerNotBuiltIn,
  setInnerSubtype,
  useFloorPlanEditorState,
  useKeepSelectedElementInSight,
} from '@kaiizen/kai-floor-plan-editor-core';
import { midtone } from '../../styles';
import { ChoiceField, CommitLengthField, CommitNumberField, ReadOnlyField, TextField } from './fields';
import { FloorTypeSelect } from './FloorTypeSelect';
import { usePanelReadOnly } from './panelReadOnly';

const CHECK_ROW_STYLE = css`
  label: CHECK_ROW_STYLE;
  display: flex; align-items: center; gap: 7px; margin-bottom: 8px;
  label { color: ${midtone}; font-size: 13px; }
`;

const NOT_BUILT_IN_LABEL = 'Not built-in (furniture / appliance)';

type ObjectElement = Extract<FloorPlanFreeElement, { customAreaType: 'object' }>;

/** Object-only top rows: name + subtype. */
function ObjectRows({ freeElement }: { freeElement: ObjectElement }): React.JSX.Element {
  const floorPlanEditorState = useFloorPlanEditorState();
  return (
    <>
      <TextField
        id='inner-name'
        label='Name'
        maxLength={40}
        placeholder='e.g. bed, fridge'
        value={freeElement.name ?? ''}
        onCommit={name => setInnerName(floorPlanEditorState, name)}
      />
      <ChoiceField
        id='inner-subtype'
        label='Type'
        value={freeElement.subtype ?? OBJECT_SUBTYPES[0]}
        options={OBJECT_SUBTYPE_OPTIONS}
        onChange={subtype => setInnerSubtype(floorPlanEditorState, subtype)}
      />
    </>
  );
}

export function FreeElementInspector({ freeElement }: { freeElement: FloorPlanFreeElement }): React.JSX.Element {
  const floorPlanEditorState = useFloorPlanEditorState();
  const keepInSight = useKeepSelectedElementInSight();
  const type = freeElement.customAreaType;
  const sized = movesInHeight(freeElement);
  const readOnly = usePanelReadOnly();
  return (
    <>
      <h2>{capitalized(type)}</h2>
      {freeElement.customAreaType === 'flooring' && (
        <FloorTypeSelect
          id='inner-floortype'
          label='Floor type'
          value={freeElement.floorType}
          onCommit={key => setInnerFloorType(floorPlanEditorState, key ?? '')}
        />
      )}
      {freeElement.customAreaType === 'object' && <ObjectRows freeElement={freeElement} />}
      <CommitLengthField label='X' setKey='inner-x' value={freeElement.x} allowNegative />
      <CommitLengthField label='Y' setKey='inner-y' value={freeElement.y} allowNegative />
      <CommitLengthField label='Width' setKey='inner-width' value={freeElement.width} />
      <CommitLengthField label='Length' setKey='inner-height' value={freeElement.length} />
      {sized && <CommitLengthField label='Height' setKey='inner-itemheight' value={freeElementHeight(freeElement)} />}
      {sized && <CommitLengthField label='Base (off floor)' setKey='inner-base' value={freeElementBase(freeElement)} />}
      <CommitNumberField label='Rotation (deg)' setKey='inner-rotation' step={5} value={fmt(freeElement.rotation ?? 0)} />
      {freeElement.customAreaType === 'object' && readOnly && (
        <ReadOnlyField id='inner-notbuiltin' label={NOT_BUILT_IN_LABEL} value={isMovableObject(freeElement) ? 'Yes' : 'No'} />
      )}
      {freeElement.customAreaType === 'object' && !readOnly && (
        <div css={CHECK_ROW_STYLE} style={{ marginTop: 8 }}>
          <input
            type='checkbox'
            id='inner-notbuiltin'
            checked={isMovableObject(freeElement)}
            onChange={changeEvent => keepInSight(() => setInnerNotBuiltIn(floorPlanEditorState, changeEvent.currentTarget.checked))}
          />
          <label htmlFor='inner-notbuiltin'>{NOT_BUILT_IN_LABEL}</label>
        </div>
      )}
    </>
  );
}
